import { useMutation } from 'react-query'
import { AIJobStatus, Export } from 'types'
import { Options as CreateOptions, Variables } from 'hooks/queries/useCreateExport'
import { useCreateExport } from 'hooks/queries/useCreateExport'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

interface Data {
  export: Export
}

export const fetchExport = async ({
  almanacFileId,
  id,
  signal,
}: {
  almanacFileId: string
  id: number
  signal?: AbortSignal
}) => {
  const res = await sendToApi<Data>(`/almanac_files/${almanacFileId}/exports/${id}`, { signal })
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

export const useCreateExportSync = <Context = unknown,>(options?: CreateOptions<Context>) => {
  const { mutateAsync: createExport } = useCreateExport()

  return useMutation<Export, Error, Variables, Context>(async (vars) => {
    const { almanacFileId, signal } = vars

    let fileExport = await createExport(vars)

    let interval = 500
    do {
      const result = await fetchExport({ id: fileExport.id, almanacFileId })

      fileExport = result.export

      if (signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError')
      }

      if (fileExport?.status === AIJobStatus.Failed) {
        throw new Error('Failed to export')
      }

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (![AIJobStatus.Failed, AIJobStatus.Expired, AIJobStatus.Successful].includes(fileExport?.status))

    return fileExport
  }, options)
}
